import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  Badge,
  Button,
  LoadingState,
  ErrorState,
  EmptyState,
} from "@/components/design-system";
import { myReferrals } from "@/lib/referrals";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_BADGE = {
  pending: { variant: "neutral", label: "Pending verify" },
  converted: { variant: "active", label: "Verified" },
  expired: { variant: "admin", label: "Expired" },
};

function Stat({ label, value, tone = "navy", testId }) {
  const tones = {
    navy: "text-[#0B1F3A]",
    blue: "text-[#2D7FF9]",
    green: "text-[#7ED957]",
    cyan: "text-[#20C7E8]",
  };
  return (
    <Card className="!p-5">
      <div className="text-[11px] font-bold uppercase tracking-widest text-[#0B1F3A]/60">
        {label}
      </div>
      <div
        data-testid={testId}
        className={`mt-2 font-[Montserrat] font-extrabold text-4xl ${tones[tone]}`}
      >
        {value}
      </div>
    </Card>
  );
}

export default function Referrals() {
  const { user } = useAuth();
  const [state, setState] = useState({ status: "loading", data: null, err: "" });
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const data = await myReferrals();
      setState({ status: "ready", data, err: "" });
    } catch (err) {
      setState({ status: "error", data: null, err: formatApiError(err) });
    }
  };
  useEffect(() => {
    load();
  }, []);

  const shareUrl = useMemo(() => {
    if (!state.data?.code) return "";
    return `${window.location.origin}/register?ref=${state.data.code}`;
  }, [state.data?.code]);

  const copy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      /* clipboard blocked */
    }
  };

  if (state.status === "loading") return <LoadingState label="Loading referrals…" />;

  // Not a verified ambassador — helpful nudge
  if (state.status === "error") {
    const notVerified = user?.ambassador_status !== "verified";
    if (notVerified) {
      return (
        <div data-testid="referrals-page">
          <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">
            Referrals
          </p>
          <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">
            Referral links are unlocked for Verified Ambassadors.
          </h1>
          <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">
            Apply and get approved to receive your unique code + 12-month attribution
            window on every subscription you bring in.
          </p>
          <div className="mt-6">
            <Link to="/app/ambassadors">
              <Button variant="primary" data-testid="referrals-cta-apply">
                Go to Ambassador programme
              </Button>
            </Link>
          </div>
        </div>
      );
    }
    return <ErrorState detail={state.err} />;
  }

  const { code, counts, attribution_window_days, attributions, created_at } = state.data;

  return (
    <div data-testid="referrals-page" className="space-y-6">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">
          Ambassador · Referral ledger
        </p>
        <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">
          Your referral link
        </h1>
        <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">
          Every signup is attributed to you for {Math.round(attribution_window_days / 30)}{" "}
          months from the moment they join. Commission payouts land in Cycle P3 (Stripe
          Connect).
        </p>
      </div>

      <Card>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="ambassador">Verified ambassador</Badge>
          <span className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60">
            Code active since {new Date(created_at).toLocaleDateString()}
          </span>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-3 items-center">
          <div className="rounded-lg bg-[#0B1F3A] text-white px-4 py-3 font-[Montserrat] font-extrabold tracking-widest text-2xl">
            <span data-testid="referral-code">{code}</span>
          </div>
          <input
            data-testid="referral-share-url"
            readOnly
            value={shareUrl}
            onFocus={(e) => e.target.select()}
            className="w-full rounded-lg border-2 border-[#0B1F3A]/15 bg-white px-4 py-3 text-sm text-[#0B1F3A] focus:border-[#2D7FF9] focus:outline-none"
          />
          <Button
            variant="primary"
            onClick={copy}
            data-testid="referral-copy-btn"
          >
            {copied ? "Copied ✓" : "Copy link"}
          </Button>
        </div>
        <p className="mt-3 text-xs text-[#0B1F3A]/60">
          Share this link on LinkedIn, X, your newsletter, or DMs. Every click is tracked
          anonymously; every verified signup enters your ledger.
        </p>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Clicks" value={counts.clicks} tone="navy" testId="stat-clicks" />
        <Stat label="Signups (pending)" value={counts.signups_pending} tone="cyan" testId="stat-pending" />
        <Stat label="Verified signups" value={counts.signups_converted} tone="green" testId="stat-converted" />
        <Stat label="Expired" value={counts.signups_expired} tone="navy" testId="stat-expired" />
      </div>

      <Card>
        <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">
          Anonymised attribution ledger
        </h2>
        <p className="text-sm text-[#0B1F3A]/70 mt-1">
          We only show pseudonyms here — real identities stay with the admin team, per
          your ambassador agreement.
        </p>
        <div className="mt-4">
          {attributions.length === 0 ? (
            <EmptyState
              title="No signups yet"
              description="Share your link. As soon as someone signs up with it, they'll appear here."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="referral-ledger-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-[#0B1F3A]/60 border-b border-[#0B1F3A]/10">
                    <th className="py-2 pr-3">Pseudonym</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Signed up</th>
                    <th className="py-2 pr-3">Verified</th>
                    <th className="py-2 pr-3">Window ends</th>
                  </tr>
                </thead>
                <tbody>
                  {attributions.map((a, i) => {
                    const meta = STATUS_BADGE[a.status] || STATUS_BADGE.pending;
                    return (
                      <tr key={i} className="border-b border-[#0B1F3A]/5">
                        <td className="py-2 pr-3 font-mono text-[13px] text-[#0B1F3A]">
                          {a.anon_label}
                        </td>
                        <td className="py-2 pr-3">
                          <Badge variant={meta.variant}>{meta.label}</Badge>
                        </td>
                        <td className="py-2 pr-3 text-[#0B1F3A]/85">
                          {a.signup_at ? new Date(a.signup_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="py-2 pr-3 text-[#0B1F3A]/85">
                          {a.converted_at
                            ? new Date(a.converted_at).toLocaleDateString()
                            : "—"}
                        </td>
                        <td className="py-2 pr-3 text-[#0B1F3A]/60">
                          {a.expires_at
                            ? new Date(a.expires_at).toLocaleDateString()
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
